import { useEffect, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, MenuItem, Divider, Chip, Autocomplete, Checkbox } from "@mui/material";
import { GroupHeader, GroupItems } from "../../../utils/Styled";
import { Countries } from "../../../utils/Lookup";
import { Icons } from "../../../utils/Icon";
import _ from "lodash";
import axios from "axios";
import Swal from "sweetalert2";

export default function EditProductGroupForm({ open, handleClose, rowData, fetchData, userCountry }) {
    const [editData, setEditData] = useState({});
    const [productsData, setProductsData] = useState([]);
    const [selectedProductValues, setSelectedProductValues] = useState([]);
    const [errors, setErrors] = useState({ group_name: false, group_description: false, country_id: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (rowData) {
            setEditData(rowData);
        }
        if (open) {
            setProductsData([]);
            setSelectedProductValues([]);
            setErrors({ group_name: false, group_description: false, country_id: false });
            setHelperText({});
            setErrorMessage("");
        }
    }, [rowData, open]);

    useEffect(() => {
        if (!open || !rowData || !editData.country_id) {
            return;
        }

        axios
            .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/products/select?country=${editData.country_id}&is_contract_product=1`)
            .then((response) => {
                const products = response.data.data.products || [];
                setProductsData(products);

                if (Number(editData.country_id) !== Number(rowData.country_id)) {
                    setSelectedProductValues([]);
                    return;
                }

                const selectedFromRow = rowData.selectedProducts
                    ? rowData.selectedProducts
                          .split(",")
                          .map((productId) => products.find((product) => product.product_id === parseInt(productId, 10)))
                          .filter(Boolean)
                    : products.filter((product) => Number(product.product_group_id) === Number(rowData.product_group_id));

                setSelectedProductValues(selectedFromRow);
            })
            .catch((error) => console.log(error));
    }, [open, rowData, editData.country_id]);

    if (!rowData) return null;

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "group_name" && value === "") {
            await setError("group_name", "Name is required.");
        } else if (field === "group_description" && value === "") {
            await setError("group_description", "Description is required.");
        } else if (field === "country_id" && value === "") {
            await setError("country_id", "Manage By is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const handleUpdate = async () => {
        try {
            const { group_name, group_description, country_id } = editData;
            const selected_products = selectedProductValues.map((product) => product.product_id);
            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/product-groups/${rowData.product_group_id}`, {
                group_name,
                group_description,
                country_id,
                selected_products,
            });

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Product group information updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating the product group information. Please try again later.");
            }
        }
    };

    const handleCountryChange = async (countryId) => {
        const hasSelectedProducts = selectedProductValues.length > 0;
        const isSameCountry = String(editData.country_id || "") === String(countryId || "");

        if (hasSelectedProducts && !isSameCountry) {
            const confirmReset = await Swal.fire({
                title: "Change Manage By?",
                text: "Changing Manage By will reset selected products.",
                icon: "warning",
                allowOutsideClick: false,
                showCancelButton: true,
                confirmButtonColor: "#fe7320",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, continue",
            });

            if (!confirmReset.isConfirmed && !confirmReset.value) {
                return;
            }
        }

        setEditData({ ...editData, country_id: countryId });
        setSelectedProductValues([]);
        validate("country_id", countryId);
    };

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit Product Group Information</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="group_name"
                    label="Name"
                    type="text"
                    fullWidth
                    required
                    value={editData.group_name || ""}
                    error={errors.group_name}
                    helperText={helperText.group_name}
                    onChange={(e) => {
                        setEditData({ ...editData, group_name: e.target.value });
                        validate("group_name", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="group_description"
                    label="Description"
                    type="text"
                    multiline
                    rows={3}
                    fullWidth
                    required
                    value={editData.group_description || ""}
                    error={errors.group_description}
                    helperText={helperText.group_description}
                    onChange={(e) => {
                        setEditData({ ...editData, group_description: e.target.value });
                        validate("group_description", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="country_id"
                    select
                    label="Manage By"
                    type="number"
                    fullWidth
                    required
                    value={_.findIndex(userCountry, { country_id: editData.country_id }) < 0 ? "" : editData.country_id || ""}
                    error={errors.country_id}
                    helperText={helperText.country_id}
                    onChange={(e) => {
                        handleCountryChange(e.target.value);
                    }}
                >
                    {userCountry.map((country) => (
                        <MenuItem key={country.country_id} value={country.country_id}>
                            {country.country_name}
                        </MenuItem>
                    ))}
                </TextField>
                <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Contract Product Assignment" />
                </Divider>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    groupBy={(option) => {
                        const country = Countries.find((country) => country.id === option.country_id);
                        return country ? country.name : "Unknown";
                    }}
                    options={productsData}
                    getOptionLabel={(option) => option.product_description ?? ""}
                    value={selectedProductValues}
                    onChange={(e, value) => setSelectedProductValues(value)}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox icon={Icons.CheckBoxBlankIcon} checkedIcon={Icons.CheckBoxCheckedIcon} style={{ marginRight: 8 }} checked={selected} />
                            {option.product_description}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} margin="dense" id="product_id" label="Product" fullWidth error={errors.product_id} helperText={helperText.product_id} />}
                    renderGroup={(params) => {
                        return (
                            <li key={params.key}>
                                <GroupHeader>{params.group}</GroupHeader>
                                <GroupItems>{params.children}</GroupItems>
                            </li>
                        );
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleUpdate} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Update
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
